import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

const Toaster = ({ style, ...props }: ToasterProps) => {
  return (
    <Sonner
      className="toaster group"
      position="top-center"
      // Pin toasts to the z-scale's toast layer. Inline style outranks
      // sonner's stylesheet default (z-index 999999999), which would otherwise
      // paint toasts over the maintenance takeover.
      style={{ ...style, zIndex: 'var(--z-index-toast)' }}
      // Clear the iOS status bar/notch in standalone PWA and the Capacitor webview.
      // env(safe-area-inset-top) is 0 on regular web, so this equals sonner's
      // default top gap (24px desktop / 16px mobile) everywhere else. Other sides
      // keep sonner's defaults.
      offset={{ top: 'calc(env(safe-area-inset-top, 0px) + 24px)' }}
      mobileOffset={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <OctagonXIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      toastOptions={{
        classNames: {
          // pointer-events-auto: a modal drawer sets `body { pointer-events: none }`, which a toast
          // would inherit, so a tap or swipe on it landed on the drawer's scrim. Not `!`: sonner's
          // unlayered `[data-visible='false'] { pointer-events: none }` must still win for a hidden
          // toast. DrawerContent keeps a tap on a toast from closing the drawer.
          // The colour and shape utilities paint only custom toasts (toast.custom: showBanner's
          // card). On a toast sonner styles itself, its unlayered sheet outranks any utility for a
          // property it sets, on the toast and on its description and buttons alike.
          toast:
            'pointer-events-auto group-[.toaster]:bg-white group-[.toaster]:text-gray-900 group-[.toaster]:border-gray-200 group-[.toaster]:shadow-lg group-[.toaster]:rounded-xl',
          // sonner paints the action button from the same variables as the toast, inverted, so on
          // a tinted toast it would take the tint: a red Retry. Retry is a safe way back, and red
          // is for destructive actions only, so the button keeps sonner's own light-theme values,
          // white on --gray12 (#171717), on every toast.
          actionButton: '[--normal-bg:var(--color-white)] [--normal-text:var(--gray12)]',
          // A typed toast is tinted by re-pointing, on that toast alone, the three variables sonner
          // paints every toast from: the fill, the border, and the title and icon.
          // Colour utilities cannot do it (TECH-742): sonner injects its stylesheet unlayered at
          // runtime, and an unlayered rule beats any rule in a cascade layer, Tailwind's
          // utilities included, whatever the specificity, so `bg-red-50` here rendered white.
          // For the same reason `richColors` or `invert` would silently beat the tint: their
          // unlayered rules set the same properties (the colours themselves, or these variables).
          // Error, warning and info take StatusPill's danger shape: a 50 fill, a 200 border, 700
          // text. Success is the brand teal: mb-teal-pale, mb-teal-light, and mb-teal-ink-hover
          // (5.55:1 on that fill; mb-teal-ink is 4.58:1, just over AA, so the darker step keeps
          // a margin).
          success:
            '[--normal-bg:var(--color-mb-teal-pale)] [--normal-border:var(--color-mb-teal-light)] [--normal-text:var(--color-mb-teal-ink-hover)]',
          error:
            '[--normal-bg:var(--color-red-50)] [--normal-border:var(--color-red-200)] [--normal-text:var(--color-red-700)]',
          warning:
            '[--normal-bg:var(--color-amber-50)] [--normal-border:var(--color-amber-200)] [--normal-text:var(--color-amber-700)]',
          info: '[--normal-bg:var(--color-blue-50)] [--normal-border:var(--color-blue-200)] [--normal-text:var(--color-blue-700)]',
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
